import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Chip, Container, Divider, IconButton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { assets } from "../../../assets";
import { colors, alphaVar } from "../../../components/ui/colors";
import { RELEASE_SCHEDULE } from "../config";
import { releases, type Release } from "../data/loadReleases";
import { useNextRelease } from "../hooks/useNextRelease";
import ReleaseMarkdown from "./ReleaseMarkdown";

const formatDate = (isoDate: string | null): string => (isoDate ? DateTime.fromISO(isoDate).toFormat("LLLL d, yyyy") : "");

interface ReleaseCardProps {
  release: Release;
  isLatest: boolean;
}

function ReleaseCard({ release, isLatest }: ReleaseCardProps) {
  return (
    <Box sx={{ position: "relative", pl: { xs: 2.5, md: 4 } }}>
      {/* Timeline dot - enable if release timeline markers are needed again.
      <Box
        sx={{
          position: "absolute",
          left: { xs: 2, md: 7 },
          top: 18,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: colors.orange.base,
          border: `2px solid var(--fx-surface-base, #FFFFFF)`,
          boxShadow: `0 0 0 4px ${alphaVar(colors.orange.base, 0.12)}`,
        }}
      />
      */}
      {/* Aqui se define la card de cada release note */}
      <Accordion
        defaultExpanded={isLatest}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          borderRadius: "10px",
          transition: "border-color 0.2s ease, transform 0.2s ease",
          overflow: "hidden",
          "&:before": { display: "none" },
          "&:hover": {
            borderColor: "var(--fx-surface-border, #E2E8F0)",
            transform: "translateY(-1px)",
          },
          "&.Mui-expanded": {
            m: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: colors.gray[400] }} />}
          sx={{
            alignItems: "flex-start",
            px: { xs: 2.25, md: 2.75 },
            py: { xs: 2, md: 2.35 },
            "& .MuiAccordionSummary-content": {
              m: 0,
              minWidth: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
            },
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0, pr: 1.5 }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Typography variant="h6" fontWeight={900} sx={{ fontFamily: "monospace", color: colors.gray.base, lineHeight: 1.1 }}>
                  v{release.version}
                </Typography>
                {isLatest && (
                  <Chip
                    label="Latest"
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.62rem",
                      color: "#fff",
                      backgroundColor: colors.orange.base,
                      height: 20,
                      borderRadius: 1,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </Stack>

              <Box sx={{ flexGrow: 1 }} />

              {release.date && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.6}
                  sx={{
                    px: 1,
                    py: 0.45,
                    borderRadius: 1,
                    border: `1px solid ${alphaVar(colors.gray[300], 0.55)}`,
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                    color: colors.gray[400],
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 13 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {formatDate(release.date)}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {release.title && (
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: colors.gray.base, lineHeight: 1.25 }}>
                {release.title}
              </Typography>
            )}
            {release.summary && (
              <Typography variant="body2" sx={{ color: colors.gray[400], mt: 0.65, lineHeight: 1.55 }}>
                {release.summary}
              </Typography>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ px: { xs: 2.25, md: 2.75 }, pt: 0, pb: { xs: 2.25, md: 2.75 } }}>
          <ReleaseMarkdown>{release.body}</ReleaseMarkdown>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default function ReleasesPage() {
  const navigate = useNavigate();
  const next = useNextRelease();
  const hasToken = Boolean(sessionStorage.getItem("token"));

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "var(--fx-surface-sunken, #F8FAFC)" }}>
      <Box
        sx={{
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          borderBottom: `1px solid ${alphaVar(colors.gray[300], 0.45)}`,
          pb: { xs: 3, md: 4 },
          pt: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            minHeight: 64,
            display: "flex",
            alignItems: "center",
            mb: { xs: 1, md: 1 },
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <IconButton onClick={() => navigate(hasToken ? -1 : "/signin")} size="small" sx={{ color: colors.gray.base }}>
                <ArrowBackIcon />
              </IconButton>
              <Avatar src={assets.images.flownecticon} sx={{ width: 30, height: 30 }} />
              <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: ".05rem", color: colors.gray.base }}>
                Flownect
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1 }}>
            <RocketLaunchIcon sx={{ fontSize: 28, color: colors.orange.base, flexShrink: 0 }} />
            <Typography variant="h4" fontWeight={800} sx={{ color: colors.gray.base, fontSize: { xs: "1.75rem", md: "2rem" }, lineHeight: 1.1 }}>
              Releases
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: colors.gray[400], maxWidth: 560, lineHeight: 1.6 }}>
            Everything we ship, in one place. A new release goes live {RELEASE_SCHEDULE.label.toLowerCase()}.
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              mt: 2.5,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              border: `1px solid ${alphaVar(colors.gray[300], 0.55)}`,
              color: colors.orange.base,
              width: "fit-content",
            }}
          >
            <ScheduleIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight={800}>
              Next release: {next.target.toFormat("cccc, LLL d")}
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pt: { xs: 3, md: 4 }, pb: 8 }}>
        <Box sx={{ position: "relative" }}>
          {/* Timeline line - enable if release timeline markers are needed again.
          <Box
            sx={{
              position: "absolute",
              left: { xs: 6, md: 11 },
              top: 18,
              bottom: 18,
              width: 2,
              backgroundColor: alphaVar(colors.orange.base, 0.18),
            }}
          />
          */}
          <Stack spacing={3.5}>
            {releases.map((release, index) => (
              <ReleaseCard key={release.version} release={release} isLatest={index === 0} />
            ))}
          </Stack>
        </Box>

        <Divider sx={{ mt: 5, mb: 2 }} />
        <Typography variant="caption" sx={{ color: colors.gray[300], display: "block", textAlign: "center" }}>
          Flownect · Release history
        </Typography>
      </Container>
    </Box>
  );
}
